import c from "classnames";
import { useState } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { Responsive } from "react-grid-layout";
import { useNavigate } from "react-router";

import {
  ClientApplicationLastUsedStat,
  ClientExtensionAdoptionRateStat,
  ClientTotalUsersStat,
} from "@components/Stats";
import { useClientExtensionAdoptionRateStatQuery } from "@components/Stats/ClientExtensionAdoptionRateStat/ClientExtensionAdoptionRateStat.g";
import {
  ApplicationsMostUsedByClientTable,
  ClientEmployeesMostActiveTable,
} from "@components/Tables";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { Lottie } from "@design-system/Lottie/Lottie";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import classes from "./ClientDashboard.module.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const layout = [
  { i: "a", x: 0, y: 0, w: 4, h: 4 },
  { i: "b", x: 4, y: 0, w: 4, h: 4 },
  { i: "c", x: 8, y: 0, w: 4, h: 4 },
  { i: "d", x: 0, y: 4, w: 12, h: 10 },
  { i: "e", x: 0, y: 16, w: 6, h: 16 },
  { i: "f", x: 6, y: 16, w: 6, h: 16 },
];

const layoutMd = [
  { i: "a", x: 0, y: 0, w: 12, h: 4 },
  { i: "b", x: 0, y: 4, w: 6, h: 4 },
  { i: "c", x: 6, y: 4, w: 6, h: 4 },
  { i: "d", x: 0, y: 8, w: 12, h: 10 },
  { i: "e", x: 0, y: 18, w: 12, h: 16 },
  { i: "f", x: 0, y: 34, w: 12, h: 16 },
];

const layoutSm = [
  { i: "a", x: 0, y: 0, w: 12, h: 4 },
  { i: "b", x: 0, y: 4, w: 12, h: 4 },
  { i: "c", x: 0, y: 8, w: 12, h: 4 },
  { i: "d", x: 0, y: 12, w: 12, h: 10 },
  { i: "e", x: 0, y: 22, w: 12, h: 16 },
  { i: "f", x: 0, y: 38, w: 12, h: 16 },
];

const layouts = {
  lg: layout,
  md: layout,
  sm: layoutMd,
  xs: layoutMd,
  xxs: layoutSm,
};

type Props = {
  clientId: string;
};

export const ClientDashboard = ({ clientId }: Props) => {
  const ref1 = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- enable once we have a way to edit the layout
  const [isEditingLayout, _setIsEditingLayout] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      if (ref1.current) {
        setWidth(ref1.current.offsetWidth);
      }
    };

    const observer = new ResizeObserver(handleResize);
    if (ref1.current) {
      observer.observe(ref1.current);
      handleResize(); // initial set
    }

    return () => observer.disconnect();
  }, []);

  const { data } = useClientExtensionAdoptionRateStatQuery({
    clientId,
  });

  const users = data?.clientEmployees?.edges?.length ?? 0;
  let activeUsers = 0;

  data?.clientEmployees?.edges?.filter((edge) => {
    if (edge?.node?.sessions?.totalCount && edge.node.sessions.totalCount > 0) {
      activeUsers += 1;
      return true;
    }
    return false;
  });

  const adoptionRate =
    users > 0 ? ((activeUsers / users) * 100).toFixed(0) : "0";

  const showExtensionModal = () => {
    navigate(
      routes.portal.clients.client.dashboard.extensionInstallation.$buildPath({
        params: { clientId },
      }),
    );
  };

  return (
    <div className={classes.container} ref={ref1}>
      <Responsive
        layouts={layouts}
        className={classes.responsive}
        rowHeight={12}
        width={width}
        cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
        isResizable={isEditingLayout}
        isDraggable={isEditingLayout}
        autoSize
        containerPadding={[0, 0]}
      >
        <div key="a" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <ClientApplicationLastUsedStat clientId={clientId} />
          </div>
        </div>

        <div key="b" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <ClientTotalUsersStat clientId={clientId} />
          </div>
        </div>

        <div key="c" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <ClientExtensionAdoptionRateStat clientId={clientId} />
          </div>
        </div>

        {adoptionRate === "0" ? (
          <div key="d" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Card>
                <Card.Header>
                  <Card.Header.Title>
                    Extension deployment instructions
                  </Card.Header.Title>
                </Card.Header>
                <Card.Body grow>
                  <Box justify="space-between" direction="row" gap={2} grow={1}>
                    <Box
                      direction="column"
                      justify="space-between"
                      grow={1}
                      stretch={true}
                    >
                      <Box direction="column" justify="flex-start">
                        <Text>Let's get you started with SaaS discovery!</Text>
                        <Text>
                          In order to collect information, Affire extension must
                          be deployed for tenant users.
                        </Text>
                        <Text>
                          Download the standalone installer and deploy it via RMM
                          tools used for specific client.
                        </Text>
                      </Box>

                      <Button
                        onClick={showExtensionModal}
                        size="lg"
                        icon="faPlus"
                      >
                        Show installation instructions
                      </Button>
                    </Box>

                    <Box>
                      <Lottie
                        lottie="script"
                        style={{ width: "100%", maxWidth: "200px" }}
                      />
                    </Box>
                  </Box>
                </Card.Body>
              </Card>
            </div>
          </div>
        ) : null}

        <div key="e" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <Card>
              <Card.Header>
                <Card.Header.Title>Most used applications</Card.Header.Title>
              </Card.Header>
              <Card.Body grow>
                <ApplicationsMostUsedByClientTable clientId={clientId} />
              </Card.Body>
            </Card>
          </div>
        </div>

        <div key="f" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <Card>
              <Card.Header>
                <Card.Header.Title>Most active users</Card.Header.Title>
              </Card.Header>
              <Card.Body grow>
                <ClientEmployeesMostActiveTable clientId={clientId} />
              </Card.Body>
            </Card>
          </div>
        </div>
      </Responsive>
    </div>
  );
};
